import React, { TextareaHTMLAttributes, useRef } from 'react';
import c from 'classnames';
import isFunction from 'lodash/isFunction';
import InputFieldOutline from '../InputFieldOutline';
import s from './TextAreaInputField.scss';

export interface Props extends Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  'prefix' | 'required'
> {
  /**
   * Adds a node below the HTML textarea tag, but within our custom input field outline.
   *
   * Separated from the textarea by a divider. Layout of items inside the footer is your responsibility.
   */
  areaFooter?: React.ReactNode;

  /** Determines whether to show a green or red border around the input. */
  validationResult?: 'success' | 'error';
  isRequired?: boolean;
  className?: string;
}

/**
 * Our standard, plaintext `<textarea />` input.
 *
 * Allows indication of success or error via `validationResult`.
 */
const TextAreaInputField = React.forwardRef<HTMLTextAreaElement, Props>(
  ({ className, isRequired, validationResult, areaFooter, disabled, ...rest }, ref) => {
    const localTextAreaRef = useRef<HTMLTextAreaElement | null>(null);
    return (
      <InputFieldOutline
        className={c(s['root'], className)}
        outlineStyle={disabled ? 'disabled' : validationResult}
        onClick={() => localTextAreaRef.current && localTextAreaRef.current.focus()}
      >
        <textarea
          required={isRequired}
          className={s['input']}
          disabled={disabled}
          ref={node => {
            if (isFunction(ref)) {
              ref(node);
            } else if (ref) {
              ref.current = node;
            }
            localTextAreaRef.current = node;
          }}
          {...rest}
        />
        {areaFooter && <div className={s['footer-divider']} aria-hidden="true" />}
        {areaFooter && <div className={s['footer']}>{areaFooter}</div>}
      </InputFieldOutline>
    );
  }
);
TextAreaInputField.displayName = 'TextAreaInputField';

export default TextAreaInputField;
